import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import DatePicker from '../components/datepicker/DatePicker';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Badge from '../components/badge/Badge';
import Table from '../components/table/table';
import { Toast, useToast } from '../components/toast/toast';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import GeneralSection from '../organisms/MonetizationNew/GeneralSection';
import LoanSection from '../organisms/MonetizationNew/LoanSection';
import ObservationSection from '../organisms/MonetizationNew/ObservationSection';
import {
  getGuaranteeTypes,
  getMonetizationFormDataByProductLineCode,
} from '../services/MonetizationService';
import {
  createSyndicatedLoan,
  getSyndicatedLines,
  previewSyndicatedInstallments,
} from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import type { FormValues, MonetizationFormData } from './MonetizationNew';
import {
  mapSyndicatedLines,
  mapSyndicatedPreviewInstallments,
  SYNDICATED_MONETIZATION_TOAST_ID,
  type SyndicatedLine,
  type SyndicatedPreviewInstallmentRow,
} from '../utils/syndicatedLines';
import { isSuccessfulResponse } from '../utils/Utils';
import {
  getEffectiveMonetizationOperationType,
  normalizeMonetizationLabel,
} from '../utils/monetizationExecution';
import {
  MONETIZATION_TYPE_ID_BY_OPERATION,
  resolveGuaranteeTypeId,
} from '../utils/monetizationGuaranteeTypes';

const TOAST_ID = SYNDICATED_MONETIZATION_TOAST_ID;

const formatPreviewNumber = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '0';
  return amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPreviewDate = (dateString: string) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const SyndicatedMonetizationNew = () => {
  const fiscalId = useFiscalIdConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const stateLine = (location.state as { line?: SyndicatedLine } | null)?.line;
  const applicationIdParam = new URLSearchParams(location.search).get('application_id');
  const applicationId = applicationIdParam ? Number(applicationIdParam) : NaN;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const stateMatchesUrl =
    !!stateLine && Number.isFinite(applicationId) && stateLine.applicationId === applicationId;
  const [line, setLine] = useState<SyndicatedLine | null>(stateMatchesUrl ? stateLine : null);
  const [lineResolved, setLineResolved] = useState(stateMatchesUrl || !applicationIdParam);
  const [formData, setFormData] = useState<MonetizationFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewSummary, setPreviewSummary] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<SyndicatedPreviewInstallmentRow[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [formValues, setFormValues] = useState<FormValues>({
    product_line_id: String(stateMatchesUrl ? stateLine?.productLineId ?? '' : ''),
    issue_date: new Date().toISOString().split('T')[0],
    created_at: new Date().toISOString().split('T')[0],
    guarantee_type_id: null,
    monetization_amount: stateMatchesUrl ? stateLine?.lineAmount ?? null : null,
    monetization_date: new Date().toISOString().split('T')[0],
    due_date: '',
    creditor_id: null,
    bank_operation_number: '',
    amortization_type_id: null,
    payment_frequency_type_id: null,
    term_in_days: null,
    rate_type_id: null,
    percentage_point: null,
    grace_period: 0,
    purpose_type_id: null,
    is_syndicated: true,
    syndicated_number: '',
    observation: '',
  });

  useEffect(() => {
    if (!applicationIdParam || !Number.isFinite(applicationId)) {
      setLineResolved(true);
      navigate(routes.monetization + '?tab=3', { replace: true });
      return;
    }

    let cancelled = false;
    const refetchQuery = stateMatchesUrl && stateLine?.cuit ? stateLine.cuit : String(applicationId);
    getSyndicatedLines(refetchQuery)
      .then((response: any) => {
        if (cancelled) return;
        const payload = response?.success ? response.data : response;
        const match = mapSyndicatedLines(payload).find((row) => row.applicationId === applicationId);
        if (!match) {
          if (!stateMatchesUrl) {
            navigate(routes.monetization + '?tab=3', { replace: true });
          }
          return;
        }
        setLine(match);
      })
      .catch(() => {
        if (!cancelled && !stateMatchesUrl) {
          navigate(routes.monetization + '?tab=3', { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLineResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, applicationIdParam, navigate]);

  useEffect(() => {
    if (!line) return;
    setFormValues((prev) => {
      const next = {
        ...prev,
        product_line_id: String(line.productLineId),
        monetization_amount: line.lineAmount,
        is_syndicated: true,
      };
      return next.product_line_id === prev.product_line_id &&
        next.monetization_amount === prev.monetization_amount &&
        prev.is_syndicated
        ? prev
        : next;
    });
    setLoading(true);
    getMonetizationFormDataByProductLineCode(line.productLineCode)
      .then((response: any) => {
        if (response?.success && response.data) {
          const data = {
            ...response.data,
            company_name: line.companyName,
            cuit: line.cuit,
            currency: line.currencyCode,
            product_line_label: line.productLabel,
          };
          setFormData(data);
        }
      })
      .finally(() => setLoading(false));
  }, [
    line?.applicationId,
    line?.companyName,
    line?.cuit,
    line?.currencyCode,
    line?.lineAmount,
    line?.productLabel,
    line?.productLineCode,
    line?.productLineId,
  ]);

  useEffect(() => {
    if (!formData?.product_line_label) return;

    const normalizedLabel = normalizeMonetizationLabel(formData.product_line_label);
    const isPublicOps = normalizedLabel === 'operaciones publicas';
    const isObligacionNegociable = normalizedLabel === 'obligacion negociable';
    const resolveByName = isPublicOps || isObligacionNegociable;
    const opType = getEffectiveMonetizationOperationType(undefined, formData.product_line_label);
    const monetizationTypeId = resolveByName ? undefined : MONETIZATION_TYPE_ID_BY_OPERATION[opType];
    const monetizationTypeName = isPublicOps
      ? 'OPERACIONES PUBLICAS'
      : isObligacionNegociable
        ? 'OBLIGACIONES NEGOCIABLES'
        : undefined;

    if (!monetizationTypeId && !monetizationTypeName) return;

    void getGuaranteeTypes(monetizationTypeId, monetizationTypeName)
      .then((response: any) => {
        if (!response?.success || !('data' in response)) return;
        const filtered = (response.data as any[]).map((gt: any) => ({
          id: gt.id,
          type: gt.type,
          label: gt.label,
          name: gt.name_end_user || gt.nameEndUser || gt.name,
          name_end_user: gt.name_end_user || gt.nameEndUser,
        }));
        setFormData((prev) => {
          if (!prev) return prev;
          const sameIds =
            prev.guarantee_types?.length === filtered.length &&
            prev.guarantee_types.every((gt, index) => gt.id === filtered[index]?.id);
          return sameIds ? prev : { ...prev, guarantee_types: filtered };
        });
        setFormValues((prev) => {
          const nextId = resolveGuaranteeTypeId(prev.guarantee_type_id, filtered);
          return nextId === prev.guarantee_type_id ? prev : { ...prev, guarantee_type_id: nextId };
        });
      })
      .catch(() => undefined);
  }, [formData?.product_line_label]);

  const updateFormValues = useCallback((updates: Partial<FormValues>) => {
    setFormValues((prev) => {
      const next = {
        ...prev,
        ...updates,
        is_syndicated: true,
        monetization_amount: prev.monetization_amount,
      };
      if (updates.issue_date && !updates.monetization_date) {
        next.monetization_date = updates.issue_date;
      }
      const changed = (Object.keys(next) as Array<keyof FormValues>).some(
        (key) => next[key] !== prev[key]
      );
      return changed ? next : prev;
    });
  }, []);

  const canConfirm = useMemo(() => {
    return Boolean(
      formValues.syndicated_number?.trim() &&
        formValues.issue_date &&
        formValues.due_date &&
        formValues.monetization_date &&
        formValues.guarantee_type_id &&
        formValues.creditor_id &&
        formValues.amortization_type_id &&
        formValues.payment_frequency_type_id &&
        formValues.rate_type_id &&
        file
    );
  }, [formValues, file]);

  const previewColumns = useMemo(
    () => [
      {
        name: 'Nº cuota',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <P type="3" variant="neutral">
              {row.installment_number}
            </P>
          </Card>
        ),
        width: '9%',
      },
      {
        name: 'F. vencimiento',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <P type="3" variant="neutral">
              {formatPreviewDate(row.due_date)}
            </P>
          </Card>
        ),
        width: '13%',
      },
      {
        name: 'Activo',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <Badge variant="neutral" type="text">
              {row.activo}
            </Badge>
          </Card>
        ),
        width: '12%',
      },
      {
        name: 'Amortización',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <P type="3" variant="neutral">
              {formatPreviewNumber(row.amortization)}
            </P>
          </Card>
        ),
        width: '14%',
      },
      {
        name: 'Interés',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <P type="3" variant="neutral">
              {formatPreviewNumber(row.interest)}
            </P>
          </Card>
        ),
        width: '12%',
      },
      {
        name: 'Total Cuota',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <P type="3" variant="neutral">
              {formatPreviewNumber(row.amount)}
            </P>
          </Card>
        ),
        width: '14%',
      },
      {
        name: 'Saldo Final',
        selector: (row: SyndicatedPreviewInstallmentRow) => (
          <Card>
            <P type="3" variant="neutral">
              {formatPreviewNumber(row.final_balance)}
            </P>
          </Card>
        ),
        width: '14%',
      },
    ],
    []
  );

  const handlePreview = async (selected: File) => {
    if (!line) return;
    const response: any = await previewSyndicatedInstallments(selected, line.lineAmount);
    if (!isSuccessfulResponse(response)) {
      const errors = response?.errors || response?.data?.errors;
      useToast.error({
        id: TOAST_ID,
        title: 'El archivo no es válido',
        description: Array.isArray(errors) ? errors.join(' ') : response?.message || 'Revisá el Excel.',
      });
      setFile(null);
      setPreviewSummary(null);
      setPreviewRows([]);
      return;
    }
    const data = response.data ?? response;
    const rows = mapSyndicatedPreviewInstallments(data.rows ?? [], line.lineAmount);
    setFile(selected);
    setWarnings(data.warnings ?? []);
    setPreviewRows(rows);
    setPreviewSummary(
      `${data.new_installments ?? data.newInstallments ?? rows.length} cuotas · diferencia ${data.difference ?? 0}`
    );
  };

  const handleSubmit = async () => {
    if (!line || !file) return;
    setSubmitting(true);
    try {
      const response: any = await createSyndicatedLoan(
        {
          application_id: line.applicationId,
          amortization_type_id: formValues.amortization_type_id,
          bank_operation_number: formValues.bank_operation_number,
          creditor_id: formValues.creditor_id,
          due_date: formValues.due_date,
          grace_period: formValues.grace_period ?? 0,
          guarantee_type_id: formValues.guarantee_type_id,
          issue_date: formValues.issue_date,
          monetization_date: formValues.monetization_date,
          payment_frequency_type_id: formValues.payment_frequency_type_id,
          percentage_point: formValues.percentage_point,
          rate_type_id: formValues.rate_type_id,
          term_in_days: formValues.term_in_days,
          purpose_type_id: formValues.purpose_type_id,
          syndicated_number: formValues.syndicated_number,
          observation: formValues.observation,
        },
        file
      );
      if (!isSuccessfulResponse(response) && response?.status >= 400) {
        useToast.error({
          id: TOAST_ID,
          title: 'No se pudo monetizar',
          description: response?.message || response?.data?.message || 'Intentá nuevamente.',
        });
        return;
      }
      const idGlobal = String(response.data?.idGlobal ?? response.data?.id_global ?? '').trim();
      navigate(`${routes.monetization}?tab=3`, {
        state: { syndicatedCreatedIdGlobal: idGlobal || undefined },
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!lineResolved || !line || loading || !formData) {
    return (
      <Card padding="4">
        <P>Cargando monetización sindicada...</P>
      </Card>
    );
  }

  return (
    <Card variant="neutral" modifier="inverted" spacing="4" width="85vw">
      <Toast id={TOAST_ID} />
      <H type="1" weight="bold">Nueva monetización sindicada</H>
      <P type="3">
        Línea {line.applicationId} · {line.companyName} · {fiscalId.label} {fiscalId.format(line.cuit)} ·{' '}
        {Number(line.lineAmount).toLocaleString('es-AR')} {line.currencyCode}
      </P>
      <P type="3">N° de aval se asigna al confirmar. Capacidad disponible = monto total de la línea.</P>
      <GeneralSection
        formData={formData}
        formValues={formValues}
        updateFormValues={updateFormValues}
        showAvalSindicado
        lockedSyndicated
      />
      <LoanSection
        formData={formData}
        formValues={formValues}
        updateFormValues={updateFormValues}
        operationType={formData.product_line_label}
        currencyCode={line.currencyCode}
        amountReadOnly
      />
      <ObservationSection formValues={formValues} updateFormValues={updateFormValues} />
      <div data-testid="syndicated-execution-section">
        <Card margin={{ top: '4' }}>
          <div
            style={{
              backgroundColor: '#E6F5F0',
              padding: '12px 16px',
              borderRadius: '8px',
              margin: '0 0 16px 0',
            }}
          >
            <H variant="secondary-2" type="4" weight="bold" modifier="strong" withMargin={false}>
              Ejecución de aval
            </H>
          </div>
          <Card margin={{ top: '3' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              <Card>
                <P withMargin={false} variant="neutral" type="4" modifier="strong">
                  Fecha de monetización
                </P>
                <Card margin={{ top: '2' }}>
                  <DatePicker
                    defaultValue={formValues.monetization_date ?? ''}
                    onChange={(date: string) => updateFormValues({ monetization_date: date })}
                    disabled={false}
                    displayFormat="DD/MM/AA"
                    minDate={formValues.issue_date ? new Date(formValues.issue_date) : undefined}
                  />
                </Card>
              </Card>
            </div>
          </Card>
        </Card>
      </div>
      <Card margin={{ top: '4' }} display="flex" flexDirection="column" gap="12px">
        <P weight="bold" withMargin={false}>Plan de cuotas CASFOG (.xlsx)</P>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          onChange={(event) => {
            const selected = event.target.files?.[0];
            if (selected) void handlePreview(selected);
          }}
        />
        <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
          Subir plan de cuotas
        </Button>
        {previewSummary ? <P withMargin={false}>{previewSummary}</P> : null}
        {previewRows.length > 0 ? (
          <Card margin={{ top: '2' }}>
            <div data-testid="syndicated-installments-preview">
              <Table
                columns={previewColumns}
                data={previewRows}
                tableData={{
                  size: previewRows.length,
                  page: 0,
                  totalElements: previewRows.length,
                  totalPages: 1,
                }}
                handleChangePage={() => {}}
                variant="normal"
                disablePagination
              />
            </div>
          </Card>
        ) : null}
        {warnings.map((warning) => (
          <P key={warning} withMargin={false}>{warning}</P>
        ))}
      </Card>
      <Card display="flex" gap="12px" margin={{ top: '4' }}>
        <Button variant="tertiary" onClick={() => navigate(`${routes.monetization}?tab=3`)}>
          Cancelar
        </Button>
        <Button variant="primary" disabled={!canConfirm || submitting} onClick={() => void handleSubmit()}>
          Confirmar y monetizar
        </Button>
      </Card>
    </Card>
  );
};

export default SyndicatedMonetizationNew;
